import { DEV, createEffect } from "solid-js"
import { Group, Mesh, MeshBasicMaterial, PlaneGeometry, Raycaster, type Intersection, type Vector2 } from "three"
import type { Nullable } from "vite-node"
import { onCleanupRemoveAndDispose } from "../../../rx/cleanup/onCleanupRemoveAndDispose"
import createSharedCachedFactory from "../../../rx/memo/createSharedCachedFactory"
import { useTileNodeListSignal } from "../../../rx/signal/useTileNodeListSignal"
import { ELEVATION_ABSOLUTE_MIN, ELEVATION_SIZE } from "../../core/entity/EntityElevation/elevation_consts"
import { VERTICES_PER_TILE } from "../consts"
import { getEngineThreeMainCamera } from "./getSetEngineThreeMainCamera"
import rotatePlaneToLayFlat from "./rotatePlaneToLayFlat"
import setObjPosition from "./setObjPosition"
import useMainScene from "./useMainScene"

const offsetX = 0.5 * VERTICES_PER_TILE
const offsetZ = 0.5 * VERTICES_PER_TILE

type RaycastTile = Mesh<PlaneGeometry, MeshBasicMaterial>

const intersects: Intersection<RaycastTile>[] = []

const useGetTileNodeIntersectionFromPointer = createSharedCachedFactory((engine) => {
  const camera = getEngineThreeMainCamera(engine)
  const raycaster = new Raycaster()

  // console.log('createGetPointerVector3Point', camera)

  const scene = useMainScene(engine)
  // const objects : Array<Mesh<PlaneGeometry, MeshBasicMaterial>> = []
  const group = new Group()
  onCleanupRemoveAndDispose(group)
  scene.add(group)
  const objects : Array<RaycastTile> = group.children as Array<RaycastTile>

  // const getGridSize = useGridWidthHeight(engine)
  const [getTileGraphNodeList] = useTileNodeListSignal(engine)

  const planeGeometrySize = VERTICES_PER_TILE * 0.97
  const planeGeometry = new PlaneGeometry(planeGeometrySize, planeGeometrySize)
  rotatePlaneToLayFlat(planeGeometry)
  const planeMaterial = new MeshBasicMaterial({
    // side: FrontSide
    visible: false,
  })
  if (DEV) {
    planeMaterial.color.set(0x777777)
  }
  createEffect(() => {
    // const { width, height } = getGridSize()
    const tileGraphNodeList = getTileGraphNodeList()
    group.clear()
    for (let i = 0; i < tileGraphNodeList.length; i++) {
      const tile = tileGraphNodeList[i]
      const mesh = new Mesh(planeGeometry, planeMaterial)
      onCleanupRemoveAndDispose(mesh)
      setObjPosition(mesh, tile, ELEVATION_ABSOLUTE_MIN)
      if (DEV) {
        mesh.name = `tile-${tile.x}-${tile.y}`
      }
      mesh.userData.tile = tile
      group.add(mesh)
      // const getElevation = tile.ents.l1.elevation
      const getFloorElevation = tile.floor_e
      createEffect(() => {
        const floor_e_value = getFloorElevation()
        mesh.position.y = floor_e_value * ELEVATION_SIZE
      })
    }
  })

  return function getTileNodeIntersectionFromPointer(
    pointerRef : Vector2,
    // debug = false
  ): Nullable<Intersection<RaycastTile>> {
    intersects.length = 0
    raycaster.setFromCamera(pointerRef, camera)
    // Intersections are returned sorted by distance, closest first.
    raycaster.intersectObjects(objects, false, intersects)

    const intersects0 = intersects[0]
    const point = intersects0?.point
    if (point) {
      point.z += offsetZ
      point.x += offsetX
      return intersects0
    } else {
      return null
    }
  }
})

export default useGetTileNodeIntersectionFromPointer
